import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";

import Grid from "@mui/material/Grid";
import { useHttpClient } from "shared/hooks/http-hook";
import { VALIDATOR_NONE, VALIDATOR_EMAIL, VALIDATOR_REQUIRE } from "shared/util/validators";
import Input from "shared/components/FormElements/Input";
import { useTranslation } from "react-i18next";
import FormFooter from "shared/components/FormElements/FormFooter";
import CheckBox from "shared/components/FormElements/CheckBox";
import MySkeleton from "components/ui/feedback/Skeleton";
import Modal from "shared/components/UIElements/Modal";
import Button from "shared/components/FormElements/Button";
import SelectInput from "shared/components/FormElements/Select";
import { AuthContext } from "shared/context/auth-context";
import { useForm } from "shared/hooks/form-hook";
import { CLIENT_STATUSES, CLIENT_ROLES, PERMISSION_TYPES, isPermissionAvailableForCompanyFeature } from "@routebot/constants";
import AreYouSureModal from "components/shared/AreYouSureModal";
import { FaPlay } from "react-icons/fa";
import "components/notifications/NotificationBell.css";
import { isInternationalTheme } from "config/ConfigUtils";
import { COMPANY_FEATURES } from "@routebot/constants";
import EmailVerificationBanner from "components/settings/EmailVerificationBanner";

const getClientIdValue = (client) => client?.id || client?._id;

const TeammateSettings = () => {
  const { isLoading, sendRequest } = useHttpClient();
  const { t } = useTranslation();
  const auth = useContext(AuthContext);

  const STUDENT_GROUP_KEYS = [PERMISSION_TYPES.ACCESS_TO_STUDENTS];

  const PERSONEL_GROUP_KEYS = [PERMISSION_TYPES.ACCESS_TO_PERSONELS];

  const INVENTORY_GROUP_KEYS = [];

  const OTHER_GROUP_KEYS = [];

  if (isPermissionAvailableForCompanyFeature(auth?.company?.features, PERMISSION_TYPES.ACCESS_TO_ACCOUNTING)) {
    STUDENT_GROUP_KEYS.push(PERMISSION_TYPES.ACCESS_TO_ACCOUNTING);
  }

  if (isPermissionAvailableForCompanyFeature(auth?.company?.features, PERMISSION_TYPES.ACCESS_TO_SHIFTS)) {
    PERSONEL_GROUP_KEYS.push(PERMISSION_TYPES.ACCESS_TO_SHIFTS);
  }

  if (isPermissionAvailableForCompanyFeature(auth?.company?.features, PERMISSION_TYPES.ACCESS_TO_TRANSFERS)) {
    PERSONEL_GROUP_KEYS.push(PERMISSION_TYPES.ACCESS_TO_TRANSFERS);
  }

  if (isPermissionAvailableForCompanyFeature(auth?.company?.features, PERMISSION_TYPES.ACCESS_TO_SERVICE_REPORTS)) {
    PERSONEL_GROUP_KEYS.push(PERMISSION_TYPES.ACCESS_TO_SERVICE_REPORTS);
  }

  if (isPermissionAvailableForCompanyFeature(auth?.company?.features, PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
    INVENTORY_GROUP_KEYS.push(PERMISSION_TYPES.ACCESS_TO_INVENTORY);
  }

  if (isPermissionAvailableForCompanyFeature(auth?.company?.features, PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY)) {
    INVENTORY_GROUP_KEYS.push(PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY);
  }

  if (isPermissionAvailableForCompanyFeature(auth?.company?.features, PERMISSION_TYPES.ACCESS_TO_LIVE)) {
    OTHER_GROUP_KEYS.push(PERMISSION_TYPES.ACCESS_TO_LIVE);
  }

  if (isPermissionAvailableForCompanyFeature(auth?.company?.features, PERMISSION_TYPES.ACCESS_TO_SERVISTESIN)) {
    OTHER_GROUP_KEYS.push(PERMISSION_TYPES.ACCESS_TO_SERVISTESIN);
  }

  if (auth?.company?.arventoConfig?.enabled) {
    OTHER_GROUP_KEYS.push(PERMISSION_TYPES.ACCESS_TO_TELEMETRY);
  }

  if (isPermissionAvailableForCompanyFeature(auth?.company?.features, PERMISSION_TYPES.ACCESS_TO_CUSTOMER_PORTAL)) {
    OTHER_GROUP_KEYS.push(PERMISSION_TYPES.ACCESS_TO_CUSTOMER_PORTAL);
  }

  const createClientOption = useCallback(
    (client) => {
      const value = getClientIdValue(client);
      if (!value) return null;

      const baseLabel = client?.name || client?.email || value;
      const emailLabel = client?.email && client?.email !== baseLabel ? ` (${client.email})` : "";
      const isSelf =
        (value && auth?.id && value === auth.id) || (value && auth?.id && value.toString?.() === auth.id.toString?.());
      const isPending = client?.status === CLIENT_STATUSES.PENDING;

      let suffix = "";
      if (isSelf) {
        suffix = ` (${t("youLabel")})`;
      } else if (isPending) {
        suffix = ` (${t("inviteNotAcceptedYet")})`;
      }

      return { value, label: `${baseLabel}${emailLabel}${suffix}` };
    },
    [auth?.id, t]
  );

  const [clients, setClients] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [permissions, setPermissions] = useState({});
  const [companySchools, setCompanySchools] = useState([]);
  const [isCompanySchoolsLoading, setIsCompanySchoolsLoading] = useState(true);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState([]);
  const [originalSchoolIds, setOriginalSchoolIds] = useState([]);
  const [isClientsLoading, setIsClientsLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteSchoolIds, setInviteSchoolIds] = useState([]);
  const [videoModal, setVideoModal] = useState({ show: false, url: null, title: "" });

  const usageVideoUrl = "https://media.servistesin.com/servistesin_ekip_uyeleri_sayfasi.mp4";

  const openVideoModal = useCallback(() => {
    setVideoModal({ show: true, url: usageVideoUrl, title: "Kullanım Videosu" });
  }, [usageVideoUrl]);

  const closeVideoModal = useCallback(() => {
    setVideoModal({ show: false, url: null, title: "" });
  }, []);

  // Invite form state
  const [inviteFormState, inviteInputHandler, setInviteFormData] = useForm(
    {
      inviteName: { value: "", isValid: false },
      inviteEmail: { value: "", isValid: false },
    },
    false
  );

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setIsClientsLoading(true);
        const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/clients`);
        const list = (responseData?.clients || []).filter((c) => c?.role !== CLIENT_ROLES.CUSTOMER_CONTACT);
        setClients(list);
        const options = list.map((c) => createClientOption(c)).filter(Boolean);
        setClientOptions(options);
        const selfClient = list.find((c) => getClientIdValue(c)?.toString?.() === auth?.id?.toString?.());
        if (selfClient) {
          const selfId = getClientIdValue(selfClient);
          setSelectedClientId(selfId);
          const perms = selfClient?.permissions || {};
          setPermissions(perms);
          setOriginalPermissions(perms);
          const schoolIds = (selfClient?.schools || [])
            .map((s) => (typeof s === "string" ? s : s?.id || s?._id || s?.value))
            .filter(Boolean);
          setSelectedSchoolIds(schoolIds);
          setOriginalSchoolIds(schoolIds);
        } else if (list.length === 1 && options.length === 1) {
          const clientId = options[0].value;
          setSelectedClientId(clientId);
          // Auto-load permissions for single client
          const perms = list[0]?.permissions || {};
          setPermissions(perms);
          setOriginalPermissions(perms);
          const schoolIds = (list[0]?.schools || [])
            .map((s) => (typeof s === "string" ? s : s?.id || s?._id || s?.value))
            .filter(Boolean);
          setSelectedSchoolIds(schoolIds);
          setOriginalSchoolIds(schoolIds);
        }
      } catch (err) {
      } finally {
        setIsClientsLoading(false);
      }
    };
    const fetchCompanySchools = async () => {
      try {
        setIsCompanySchoolsLoading(true);
        const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/schools/bycompany`);
        const schools = responseData?.schools || [];
        setCompanySchools(schools);
      } catch (err) {
      } finally {
        setIsCompanySchoolsLoading(false);
      }
    };
    fetchClients();
    fetchCompanySchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClientChange = useCallback(
    (id, value) => {
      // Same selection: skip resetting current changes
      if (
        (selectedClientId && value && selectedClientId === value) ||
        selectedClientId?.toString?.() === value?.toString?.()
      ) {
        return;
      }

      setSelectedClientId(value);
      const selected = clients.find((c) => (c.id || c._id) === value);
      const perms = selected?.permissions || {};
      setPermissions(perms);
      setOriginalPermissions(perms);
      const schoolIds = (selected?.schools || [])
        .map((s) => (typeof s === "string" ? s : s?.id || s?._id || s?.value))
        .filter(Boolean);
      setSelectedSchoolIds(schoolIds);
      setOriginalSchoolIds(schoolIds);
    },
    [clients, selectedClientId]
  );

  const onPermissionToggle = useCallback((id, value) => {
    const key = id.startsWith("perm_") ? id.substring(5) : id;
    setPermissions((prev) => ({ ...prev, [key]: Boolean(value) }));
  }, []);

  const onSchoolToggle = useCallback((id, value) => {
    const schoolId = id.startsWith("school_") ? id.substring(7) : id;
    setSelectedSchoolIds((prev) => {
      if (value) {
        if (prev.includes(schoolId)) return prev;
        return [...prev, schoolId];
      }
      return prev.filter((x) => x !== schoolId);
    });
  }, []);

  const hasChanges = useMemo(() => {
    // permissions diff
    const keys = Array.from(new Set([...Object.keys(originalPermissions || {}), ...Object.keys(permissions || {})]));
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (Boolean(originalPermissions?.[k]) !== Boolean(permissions?.[k])) return true;
    }
    // schools diff
    const initSchools = [...(originalSchoolIds || [])].sort();
    const currSchools = [...(selectedSchoolIds || [])].sort();
    if (initSchools.length !== currSchools.length) return true;
    for (let i = 0; i < initSchools.length; i++) {
      if (initSchools[i] !== currSchools[i]) return true;
    }
    return false;
  }, [originalPermissions, permissions, originalSchoolIds, selectedSchoolIds]);

  const selectedClient = useMemo(
    () => clients.find((c) => (c.id || c._id) === selectedClientId),
    [clients, selectedClientId]
  );

  const isSelfSelected = useMemo(() => {
    if (!selectedClientId || !auth?.id) return false;
    return (
      selectedClientId === auth.id ||
      selectedClientId.toString?.() === auth.id.toString?.() ||
      auth.id.toString?.() === (selectedClient?._id || selectedClient?.id)?.toString?.()
    );
  }, [selectedClientId, auth?.id, selectedClient?._id, selectedClient?.id]);

  const isPendingClient = selectedClient?.status === CLIENT_STATUSES.PENDING;

  const deleteCtaText = useMemo(() => (isPendingClient ? t("revokeInvite") : t("removeUser")), [isPendingClient, t]);

  const deleteModalText = useMemo(
    () => (isPendingClient ? t("revokeInviteConfirmation") : t("removeUserConfirmation")),
    [isPendingClient, t]
  );

  const onDelete = useCallback(() => {
    if (!selectedClientId || isDeleting) return;
    setShowDeleteModal(true);
  }, [selectedClientId, isDeleting]);

  const onDeleteConfirmed = useCallback(async () => {
    if (!selectedClientId) return;
    setIsDeleting(true);
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/clients/${selectedClientId}`, "DELETE");

      setClients((prev) => prev.filter((c) => (c.id || c._id) !== selectedClientId));
      setClientOptions((prev) => prev.filter((o) => o.value !== selectedClientId));

      // Clear selection if the deleted client was selected; then fallback to self if available
      if (selectedClientId) {
        setSelectedClientId(null);
        setPermissions({});
        setOriginalPermissions({});
        setSelectedSchoolIds([]);
        setOriginalSchoolIds([]);
      }

      const selfClient = clients.find((c) => getClientIdValue(c)?.toString?.() === auth?.id?.toString?.());
      if (selfClient) {
        const selfId = getClientIdValue(selfClient);
        setSelectedClientId(selfId);
        const perms = selfClient?.permissions || {};
        setPermissions(perms);
        setOriginalPermissions(perms);
        const schoolIds = (selfClient?.schools || [])
          .map((s) => (typeof s === "string" ? s : s?.id || s?._id || s?.value))
          .filter(Boolean);
        setSelectedSchoolIds(schoolIds);
        setOriginalSchoolIds(schoolIds);
      }

      if (window?.toast?.success) window.toast.success(t("deleteSuccess"));
    } catch (err) {
      if (window?.toast?.error) window.toast.error(t("actionFailed"));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [selectedClientId, sendRequest, t]);

  const performSubmit = useCallback(async () => {
    if (!selectedClientId) return;
    setIsSubmitting(true);
    try {
      const payload = { permissions, schools: selectedSchoolIds };
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/clients/${selectedClientId}`,
        "PATCH",
        JSON.stringify(payload),
        {
          "Content-Type": "application/json",
        }
      );
      if (window?.toast?.success) window.toast.success(t("updateSuccess"));
      // update local originals
      setOriginalPermissions(permissions);
      setOriginalSchoolIds(selectedSchoolIds);

      if (
        (selectedClientId && auth?.id && selectedClientId === auth.id) ||
        (selectedClientId && auth?.id && selectedClientId.toString?.() === auth.id.toString?.())
      ) {
        window.location.href = "/settings";
      }
    } catch (err) {
      if (window?.toast?.error) window.toast.error(t("actionFailed"));
    } finally {
      setIsSubmitting(false);
    }
  }, [permissions, selectedClientId, sendRequest, t, selectedSchoolIds, auth?.id]);

  const onSubmit = useCallback(async () => {
    if (!hasChanges || !selectedClientId) return;
    setShowConfirm(true);
  }, [hasChanges, selectedClientId]);

  const accessibleInviteSchools = useMemo(() => {
    if (!Array.isArray(companySchools)) return [];
    return companySchools;
  }, [companySchools]);

  useEffect(() => {
    if (showInviteModal) {
      // Modal açıldığında boş başla; kullanıcı en az bir okul seçmeli
      setInviteSchoolIds([]);
    } else {
      setInviteSchoolIds([]);
    }
  }, [showInviteModal, accessibleInviteSchools]);

  const { studentGroupKeys, personelGroupKeys, inventoryGroupKeys, otherPermissionKeys } = useMemo(() => {
    const pickGroupKeys = (keys) => [...keys].sort((a, b) => keys.indexOf(a) - keys.indexOf(b));

    const studentKeys = pickGroupKeys(STUDENT_GROUP_KEYS);
    const personelKeys = pickGroupKeys(PERSONEL_GROUP_KEYS);
    const inventoryKeys = pickGroupKeys(INVENTORY_GROUP_KEYS);
    const others = pickGroupKeys(OTHER_GROUP_KEYS);

    return {
      studentGroupKeys: studentKeys,
      personelGroupKeys: personelKeys,
      inventoryGroupKeys: inventoryKeys,
      otherPermissionKeys: others,
    };
  }, [permissions, STUDENT_GROUP_KEYS, PERSONEL_GROUP_KEYS, INVENTORY_GROUP_KEYS, OTHER_GROUP_KEYS]);

  const handleInviteSubmit = useCallback(async () => {
    if (accessibleInviteSchools.length === 0) {
      if (window?.toast?.error) window.toast.error(t("noAccessibleSchools"));
      return;
    }

    if (!inviteFormState.inputs.inviteEmail.value) {
      if (window?.toast?.error) window.toast.error(t("emailIsRequired"));
      return;
    }

    if (inviteSchoolIds.length === 0) {
      if (window?.toast?.error) window.toast.error(t("atLeastOneSchoolRequired"));
      return;
    }

    setIsInviting(true);
    try {
      const currentLanguage = localStorage.getItem("i18nextLng") || "tr";
      const payload = {
        name: inviteFormState.inputs.inviteName.value,
        email: inviteFormState.inputs.inviteEmail.value,
        schools: inviteSchoolIds,
        language: currentLanguage,
        // istersen ileride role, schools, permissions da ekleyebilirsin
      };

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/clients/invite-teammate`,
        "POST",
        JSON.stringify(payload),
        {
          "Content-Type": "application/json",
        }
      );

      const newClient = responseData?.client;
      if (newClient) {
        const newOption = createClientOption(newClient);
        if (!newOption) {
          throw new Error("Invalid client option");
        }

        // listeye ekle
        setClients((prev) => [...prev, newClient]);
        setClientOptions((prev) => [...prev, newOption]);

        // yeni kullanıcıyı otomatik seç
        setSelectedClientId(newOption.value);
        const perms = newClient?.permissions || {};
        setPermissions(perms);
        setOriginalPermissions(perms);

        const schoolIds = (newClient?.schools || [])
          .map((s) => (typeof s === "string" ? s : s?.id || s?._id || s?.value))
          .filter(Boolean);
        setSelectedSchoolIds(schoolIds);
        setOriginalSchoolIds(schoolIds);
      }

      if (window?.toast?.success) window.toast.success(t("inviteSentSuccessfully"));
      setShowInviteModal(false);
      // Reset form
      setInviteFormData(
        {
          inviteName: { value: "", isValid: false },
          inviteEmail: { value: "", isValid: false },
        },
        false
      );
    } catch (err) {
      if (window?.toast?.error) window.toast.error(t("actionFailed"));
    } finally {
      setIsInviting(false);
    }
  }, [
    inviteFormState.inputs.inviteName.value,
    inviteFormState.inputs.inviteEmail.value,
    inviteSchoolIds,
    accessibleInviteSchools,
    sendRequest,
    t,
    setInviteFormData,
  ]);

  const isEmailVerified = auth.client?.emailConfirmed;
  const isDemo = auth.company?.isDemo;

  return (
    <React.Fragment>
      {/* Video Modal */}
      {videoModal.show && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <div className="video-modal-title">{videoModal.title || "Video"}</div>
              <button className="video-modal-close" onClick={closeVideoModal}>
                ×
              </button>
            </div>
            <video className="video-modal-player" src={videoModal.url} controls autoPlay playsInline />
          </div>
        </div>
      )}

      <EmailVerificationBanner
        className="centered-form-padding"
        containerStyle={{
          maxWidth: "1000px",
          width: "100%",
          marginTop: "20px",
        }}
      />

      <form
        className="centered-form-padding"
        style={{
          maxWidth: "1400px",
          width: "100%",
          paddingTop: "10px",
          paddingBottom: "0px",
          position: "relative",
          ...(isEmailVerified && !isDemo
            ? {}
            : {
                opacity: 0.5,
                pointerEvents: "none",
                filter: "grayscale(30%)",
                userSelect: "none",
              }),
        }}
      >
        <Modal
          show={showConfirm}
          onCancel={() => setShowConfirm(false)}
          header={t("areYouSure")}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "40px" }}>
              <Button type="button" onClick={() => setShowConfirm(false)}>
                {t("no")}
              </Button>
              <Button
                type="button"
                danger
                onClick={() => {
                  setShowConfirm(false);
                  performSubmit();
                }}
              >
                {t("yes")}
              </Button>
            </div>
          }
        >
          <div>{t("permissionChangeConfirmation")}</div>
        </Modal>

        <AreYouSureModal
          showModal={showDeleteModal}
          text={deleteModalText}
          onClose={() => setShowDeleteModal(false)}
          onConfirmed={onDeleteConfirmed}
          title={isPendingClient ? t("revokeInvite") : t("removeUser")}
        />

        <Modal
          show={showInviteModal}
          onCancel={() => {
            setShowInviteModal(false);
            setInviteFormData(
              {
                inviteName: { value: "", isValid: false },
                inviteEmail: { value: "", isValid: false },
              },
              false
            );
          }}
          header={t("inviteNewUser")}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "40px" }}>
              <Button
                type="button"
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteFormData(
                    {
                      inviteName: { value: "", isValid: false },
                      inviteEmail: { value: "", isValid: false },
                    },
                    false
                  );
                  setInviteSchoolIds([]);
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                type="button"
                danger
                disabled={!inviteFormState.isValid || inviteSchoolIds.length === 0 || isInviting}
                onClick={async () => await handleInviteSubmit()}
              >
                {isInviting ? t("sending") : t("sendInvite")}
              </Button>
            </div>
          }
        >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Input
                id="inviteName"
                element="input"
                type="text"
                label={t("name") + " *"}
                initialValue={inviteFormState.inputs.inviteName.value}
                initialValid={false}
                onInput={inviteInputHandler}
                validators={[VALIDATOR_REQUIRE()]}
                errorText={t("requireField", { item: t("name") })}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                id="inviteEmail"
                element="input"
                type="text"
                label={t("email") + " *"}
                initialValue={inviteFormState.inputs.inviteEmail.value}
                initialValid={false}
                onInput={inviteInputHandler}
                validators={[VALIDATOR_EMAIL()]}
                errorText={t("requireField", { item: t("email") })}
              />
            </Grid>

            <Grid item xs={12}>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontWeight: 600 }}>{t("userAccessibleSchools")}</label>
                {accessibleInviteSchools.length === 0 ? (
                  <div style={{ color: "gray", fontSize: "0.9rem" }}>{t("noAccessibleSchools")}</div>
                ) : (
                  accessibleInviteSchools.map((s) => {
                    const id = (s.id || s._id || s.value)?.toString?.();
                    return (
                      <CheckBox
                        key={`invite_school_${id}`}
                        id={`invite_school_${id}`}
                        label={s.name}
                        initialValue={inviteSchoolIds.includes(id)}
                        initialValid={true}
                        onInput={(checkboxId, value) => {
                          const schoolId = checkboxId.replace("invite_school_", "");
                          setInviteSchoolIds((prev) => {
                            if (value) {
                              if (prev.includes(schoolId)) return prev;
                              return [...prev, schoolId];
                            }
                            return prev.filter((x) => x !== schoolId);
                          });
                        }}
                        validators={[VALIDATOR_NONE()]}
                        labelStyle={{ marginTop: "0" }}
                      />
                    );
                  })
                )}
              </div>
            </Grid>
          </Grid>
        </Modal>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: "16px",
                }}
              >
                <h2 style={{ marginTop: "0" }}>{t("teamMates")}</h2>

                {!isInternationalTheme() && auth.company?.features[COMPANY_FEATURES.WEBSITE_NOTIFICATIONS] && (
                  <button type="button" className="notification-video-btn" onClick={openVideoModal}>
                    <FaPlay size={12} />
                    <span>Kullanım Videosu</span>
                  </button>
                )}
              </div>

              {/* Sadece yetki verebilenler görsün istersen */}
              {(auth?.permissions?.accessToGivePermissions || auth.company?.isDemo) && (
                <Button type="button" onClick={() => setShowInviteModal(true)} containerStyle={{ marginTop: "0px" }}>
                  {t("inviteNewUser") /* çeviri ekle: inviteNewUser: "Yeni Kullanıcı Davet Et" */}
                </Button>
              )}
            </div>
          </Grid>

          <Grid item xs={12} md={12} sx={{ display: "flex", gap: 2, alignItems: "center", marginTop: "10px" }}>
            <div style={{ flex: 1, maxWidth: "600px" }}>
              {isClientsLoading ? (
                <MySkeleton lineCount={1} />
              ) : (
                <SelectInput
                  key={selectedClientId}
                  id="clientSelect"
                  label={t("selectClient")}
                  options={clientOptions}
                  onInput={onClientChange}
                  validators={[VALIDATOR_NONE()]}
                  disabled={isLoading}
                  initialValue={selectedClientId ? clientOptions.find((c) => c.value === selectedClientId) : null}
                />
              )}
            </div>
          </Grid>

          {isClientsLoading ? (
            <Grid item xs={12}>
              <MySkeleton lineCount={3} />
            </Grid>
          ) : selectedClientId ? (
            <Grid item xs={12}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    {studentGroupKeys.length > 0 && (
                      <>
                        <Grid item xs={12}>
                          <h4 style={{ marginTop: "10px", marginBottom: "0" }}>{t("permissionsGroup.students")}</h4>
                        </Grid>

                        <Grid item xs={12}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            {studentGroupKeys.map((key) => (
                              <CheckBox
                                key={`${selectedClientId}_${key}`}
                                id={`perm_${key}`}
                                label={t(`permissions.${key}`)}
                                initialValue={Boolean(permissions?.[key])}
                                initialValid={true}
                                onInput={onPermissionToggle}
                                validators={[VALIDATOR_NONE()]}
                                labelStyle={{ marginTop: "0" }}
                              />
                            ))}
                          </div>
                        </Grid>
                      </>
                    )}

                    {personelGroupKeys.length > 0 && (
                      <>
                        <Grid item xs={12}>
                          <h4 style={{ marginTop: "20px", marginBottom: "0" }}>{t("permissionsGroup.personel")}</h4>
                        </Grid>

                        {/* Buradan sonrası: ayrı bir satırda, 1x4 eşit grid */}
                        <Grid item xs={12}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            {personelGroupKeys.map((key) => (
                              <CheckBox
                                key={`${selectedClientId}_${key}`}
                                id={`perm_${key}`}
                                label={t(`permissions.${key}`)}
                                initialValue={Boolean(permissions?.[key])}
                                initialValid={true}
                                onInput={onPermissionToggle}
                                validators={[VALIDATOR_NONE()]}
                                labelStyle={{ marginTop: "0" }}
                              />
                            ))}
                          </div>
                        </Grid>
                      </>
                    )}
                    {inventoryGroupKeys.length > 0 && (
                      <>
                        <Grid item xs={12}>
                          <h4 style={{ marginTop: "20px", marginBottom: "0" }}>{t("permissionsGroup.inventory")}</h4>
                        </Grid>
                        <Grid item xs={12}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            {inventoryGroupKeys.map((key) => (
                              <CheckBox
                                key={`${selectedClientId}_${key}`}
                                id={`perm_${key}`}
                                label={t(`permissions.${key}`)}
                                initialValue={Boolean(permissions?.[key])}
                                initialValid={true}
                                onInput={onPermissionToggle}
                                validators={[VALIDATOR_NONE()]}
                                labelStyle={{ marginTop: "0" }}
                              />
                            ))}
                          </div>
                        </Grid>
                      </>
                    )}

                    {otherPermissionKeys.length > 0 && (
                      <>
                        <Grid item xs={12}>
                          <h4 style={{ marginTop: "20px", marginBottom: "0" }}>{t("permissionsGroup.other")}</h4>
                        </Grid>
                        <Grid item xs={12}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {otherPermissionKeys.map((key) => (
                              <CheckBox
                                key={`${selectedClientId}_${key}`}
                                id={`perm_${key}`}
                                label={t(`permissions.${key}`)}
                                initialValue={Boolean(permissions?.[key])}
                                initialValid={true}
                                onInput={onPermissionToggle}
                                validators={[VALIDATOR_NONE()]}
                                labelStyle={{ marginTop: "0" }}
                              />
                            ))}
                          </div>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} style={{ marginBottom: "16px", paddingLeft: "0px" }}>
                      <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>{t("changeYourVisibleSchools")}</h4>
                    </Grid>

                    {isCompanySchoolsLoading ? (
                      <Grid item xs={12}>
                        <MySkeleton lineCount={12} />
                      </Grid>
                    ) : companySchools.length === 0 ? (
                      <Grid item xs={12}>
                        <p style={{ margin: "10px 0", marginTop: "0px" }}>{t("youDonHaveAnyClients")}</p>
                        <Button to="/schools">{t("addClient")}</Button>
                      </Grid>
                    ) : (
                      <Grid container spacing={1}>
                        {companySchools.map((s) => (
                          <Grid key={`${selectedClientId}_school_${s.id || s._id}`} item xs={12} md={6}>
                            <CheckBox
                              id={`school_${s.id || s._id}`}
                              label={s.name}
                              initialValue={selectedSchoolIds.includes(s.id || s._id)}
                              initialValid={true}
                              onInput={onSchoolToggle}
                              validators={[VALIDATOR_NONE()]}
                              labelStyle={{ marginTop: "0" }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : null}

          {!isClientsLoading && selectedClientId ? (
            <Grid item xs={12}>
              <FormFooter
                isNewItem={isSelfSelected}
                onUpdate={onSubmit}
                isUpdating={isSubmitting}
                deleteText={deleteCtaText}
                onDelete={onDelete}
                disabled={!hasChanges || !selectedClientId || isLoading || isSubmitting}
              />
            </Grid>
          ) : null}
        </Grid>
      </form>
    </React.Fragment>
  );
};

export default TeammateSettings;
